import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, fmtMoney, CLOSED_LOST_REASONS } from "@/lib/api";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { XCircle, TrendingDown, ChevronRight } from "lucide-react";

const REASON_COLORS = {
  Competitor: "bg-rose-500",
  Price: "bg-amber-500",
  "Budget Restrictions": "bg-orange-500",
  Technology: "bg-sky-500",
  "(No reason)": "bg-slate-400",
};

/**
 * Closed Lost analysis — grouped by `closed_lost_reason` for the selected
 * period. Shows a stacked-bar summary, the per-reason cards, and a sample of
 * the top 3 lost deals under each reason for quick drill-down.
 */
export default function LostReasonsCard({ owners = [] }) {
  const [period, setPeriod] = useState("ytd");
  const [ownerFilter, setOwnerFilter] = useState("all");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const nav = useNavigate();

  useEffect(() => {
    let cancel = false;
    (async () => {
      setLoading(true);
      try {
        const params = { period };
        if (ownerFilter !== "all") params.owner_name = ownerFilter;
        const r = await api.get("/dashboard/closed-lost-reasons", { params });
        if (!cancel) setData(r.data);
      } catch (_) {
        if (!cancel) setData(null);
      } finally {
        if (!cancel) setLoading(false);
      }
    })();
    return () => { cancel = true; };
  }, [period, ownerFilter]);

  const totalCount = data?.total_count || 0;
  const totalRevenue = data?.total_revenue || 0;
  const reasons = data?.by_reason || [];

  return (
    <Card className="border-rose-200" data-testid="lost-reasons-card">
      <CardHeader className="border-b border-rose-100 py-3 bg-rose-50/40">
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <CardTitle className="text-base font-heading flex items-center gap-2">
            <TrendingDown className="h-4 w-4 text-rose-600" />
            Closed Lost by reason
            <span className="text-xs font-mono text-rose-700 font-normal ml-1">
              {totalCount} deals · {fmtMoney(totalRevenue)} lost
            </span>
          </CardTitle>
          <div className="flex items-center gap-2">
            <Select value={period} onValueChange={setPeriod}>
              <SelectTrigger className="h-8 w-40 text-xs bg-white" data-testid="lost-reasons-period">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="current_month">This month</SelectItem>
                <SelectItem value="current_quarter">This quarter</SelectItem>
                <SelectItem value="ytd">YTD (FY)</SelectItem>
                <SelectItem value="all">All time</SelectItem>
              </SelectContent>
            </Select>
            <Select value={ownerFilter} onValueChange={setOwnerFilter}>
              <SelectTrigger className="h-8 w-40 text-xs bg-white" data-testid="lost-reasons-owner">
                <SelectValue placeholder="All owners" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All owners</SelectItem>
                {owners.map((o) => (
                  <SelectItem key={o} value={o}>{o}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>
      </CardHeader>
      <CardContent className="p-5 space-y-5">
        {loading && (
          <div className="text-xs text-slate-500 font-mono text-center py-4">Loading…</div>
        )}

        {!loading && totalCount === 0 && (
          <div className="text-sm text-slate-500 text-center py-8" data-testid="lost-reasons-empty">
            No Closed Lost deals in this period. When deals fall through, tag them with a reason
            (Competitor / Price / Budget Restrictions / Technology) inside the deal edit dialog
            and they&apos;ll show up here.
          </div>
        )}

        {!loading && totalCount > 0 && (
          <>
            {/* Stacked bar showing share of losses per reason */}
            <div className="flex h-3 rounded-full overflow-hidden bg-slate-100" data-testid="lost-reasons-bar">
              {reasons.map((r) => (
                <div
                  key={r.reason}
                  className={REASON_COLORS[r.reason] || "bg-slate-400"}
                  style={{ width: `${r.pct_of_count}%` }}
                  title={`${r.reason}: ${r.count} (${r.pct_of_count}%)`}
                />
              ))}
            </div>

            {/* Reason grid */}
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
              {reasons.map((r) => (
                <div
                  key={r.reason}
                  className="rounded border border-slate-200 bg-white p-3 space-y-2"
                  data-testid={`lost-reason-${r.reason.toLowerCase().replace(/\s+/g, '-').replace(/[()]/g, '')}`}
                >
                  <div className="flex items-center gap-2">
                    <span className={`inline-block w-2.5 h-2.5 rounded-full ${REASON_COLORS[r.reason] || "bg-slate-400"}`}></span>
                    <div className="font-medium text-sm text-slate-900">{r.reason}</div>
                    <div className="text-[10px] font-mono uppercase tracking-wider text-slate-400 ml-auto">
                      {r.pct_of_count}%
                    </div>
                  </div>
                  <div className="flex items-baseline gap-2">
                    <span className="text-2xl font-heading font-bold text-slate-900">{r.count}</span>
                    <span className="text-xs text-slate-500">deals</span>
                  </div>
                  <div className="text-xs text-slate-600 font-mono">
                    Revenue: <b className="text-slate-900">{fmtMoney(r.revenue_total)}</b>
                    <span className="text-slate-400 mx-1">·</span>
                    MRR: <b className="text-slate-900">{fmtMoney(r.mrr_total)}</b>
                  </div>
                  {r.sample_deals && r.sample_deals.length > 0 && (
                    <div className="pt-2 border-t border-slate-100 space-y-1">
                      <div className="text-[10px] font-mono uppercase tracking-wider text-slate-400">
                        Sample deals
                      </div>
                      {r.sample_deals.slice(0, 3).map((d) => (
                        <button
                          key={d.id}
                          onClick={() => nav(`/app/accounts/${d.account_id}`)}
                          className="w-full text-left text-xs text-slate-700 hover:text-blue-700 truncate flex items-center gap-1 group"
                          data-testid={`lost-sample-${d.id}`}
                          title={`${d.name} · ${d.account_name}`}
                        >
                          <ChevronRight className="h-3 w-3 text-slate-300 group-hover:text-blue-500 shrink-0" />
                          <span className="truncate">
                            <span className="font-medium">{d.account_name}</span>
                            <span className="text-slate-400"> · </span>
                            <span>{fmtMoney(d.revenue)}</span>
                          </span>
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              ))}
            </div>

            {/* Nudge banner about untagged deals */}
            {reasons.find((r) => r.reason === "(No reason)" && r.count > 0) && (
              <div
                className="rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-900 flex items-start gap-2"
                data-testid="lost-reasons-nudge"
              >
                <XCircle className="h-4 w-4 text-amber-600 shrink-0 mt-0.5" />
                <span>
                  {reasons.find((r) => r.reason === "(No reason)").count} Closed Lost deal(s)
                  have no reason tagged. Open the deal and pick one from the dropdown
                  (Competitor / Price / Budget Restrictions / Technology) to surface it here.
                </span>
              </div>
            )}
          </>
        )}
      </CardContent>
    </Card>
  );
}
